import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { trackSectionOpened } from '../lib/analytics';
import { Wine, Coffee, UtensilsCrossed, ShoppingBag, Sparkles, ChevronRight } from 'lucide-react';
import { MomentoPopup, MomentoBanner } from './ModoMomento';
import ExperiencesShowcase from './ExperiencesShowcase';
import ExperienceDetailPopup from './ExperienceDetailPopup';
import { prefetchCategories } from './CategoryView';

const allExperiences = [
  {
    id: 'degustacion',
    icon: UtensilsCrossed,
    image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80',
    names: { es: 'Degustaci\u00f3n', en: 'Tasting', cat: 'Degustaci\u00f3', fr: 'D\u00e9gustation' },
    subtitles: {
      es: 'Nuestro ritual gastron\u00f3mico',
      en: 'Our gastronomic ritual',
      cat: 'El nostre ritual gastron\u00f2mic',
      fr: 'Notre rituel gastronomique',
    },
    slot: 'comida',
  },
  {
    id: 'bar',
    icon: Wine,
    image: 'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=800&q=80',
    names: { es: 'Bar', en: 'Bar', cat: 'Bar', fr: 'Bar' },
    subtitles: {
      es: 'Vermut \u00b7 vinos \u00b7 momentos',
      en: 'Vermouth \u00b7 wines \u00b7 moments',
      cat: 'Vermut \u00b7 vins \u00b7 moments',
      fr: 'Vermouth \u00b7 vins \u00b7 moments',
    },
    slot: 'noche',
  },
  {
    id: 'cafe',
    icon: Coffee,
    image: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=800&q=80',
    names: { es: 'Cafeter\u00eda', en: 'Cafeteria', cat: 'Cafeteria', fr: 'Caf\u00e9t\u00e9ria' },
    subtitles: {
      es: 'Caf\u00e9 de especialidad \u00b7 dulce artesanal',
      en: 'Specialty coffee \u00b7 artisan pastry',
      cat: 'Caf\u00e8 d\'especialitat \u00b7 dol\u00e7 artesanal',
      fr: 'Caf\u00e9 de sp\u00e9cialit\u00e9 \u00b7 douceur artisanale',
    },
    slot: 'manana',
  },
  {
    id: 'tienda',
    icon: ShoppingBag,
    image: 'https://images.pexels.com/photos/12455049/pexels-photo-12455049.jpeg?auto=compress&cs=tinysrgb&w=800',
    names: { es: 'Tienda', en: 'Shop', cat: 'Botiga', fr: 'Boutique' },
    subtitles: {
      es: 'Producto local seleccionado',
      en: 'Curated local products',
      cat: 'Producte local seleccionat',
      fr: 'Produits locaux s\u00e9lectionn\u00e9s',
    },
    slot: 'always',
  },
];

// Fixed order: degustación → bar → cafetería → tienda
function getTimeBasedOrder() {
  return ['degustacion', 'bar', 'cafe', 'tienda'];
}

export const ExperienceSelector = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const [showExperiencesShowcase, setShowExperiencesShowcase] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [showMomentoPopup, setShowMomentoPopup] = useState(true);
  const [bannerExperience, setBannerExperience] = useState(null);

  useEffect(() => {
    const popupShown = sessionStorage.getItem('momento_popup_shown');
    if (popupShown) setShowMomentoPopup(false);
    // Pre-fetch categories for all sections so first click is instant
    prefetchCategories();
  }, []);

  const handlePopupSkip = () => {
    setShowMomentoPopup(false);
    sessionStorage.setItem('momento_popup_shown', 'true');
  };

  const handlePopupViewSelection = () => {
    setShowMomentoPopup(false);
    sessionStorage.setItem('momento_popup_shown', 'true');
  };

  const handleExperienceClick = (experienceId) => {
    trackSectionOpened(experienceId);
    navigate(`menu/${experienceId}`);
  };

  // Dynamic card order based on time of day
  const experiences = useMemo(() => {
    const order = getTimeBasedOrder();
    return order.map(id => allExperiences.find(e => e.id === id)).filter(Boolean);
  }, []);

  if (showMomentoPopup) {
    return <MomentoPopup onViewSelection={handlePopupViewSelection} onSkip={handlePopupSkip} />;
  }

  if (showExperiencesShowcase) {
    return <ExperiencesShowcase onBack={() => setShowExperiencesShowcase(false)} />;
  }

  return (
    <div 
      className="min-h-dvh grain safe-bottom transition-colors duration-300 pb-8 flex flex-col"
      style={{
        backgroundColor: isDark ? '#1a1a1a' : 'var(--sand-50)',
        // Clear the fixed TopCartBar (taller on tablet) + iOS safe-area.
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 96px)',
      }}
      data-testid="experience-selector-screen"
    >
      {/* Centering spacer (auto top/bottom) — auto margin gives "soft" centering
          that does NOT compress children when content exceeds the viewport. */}
      <div className="my-auto w-full">
      {/* Hero — guided title */}
      <div className="text-center pb-4 px-4">
        <h1 
          className="font-serif text-4xl md:text-5xl animate-fade-up transition-colors duration-300"
          style={{ color: isDark ? '#f5f5f5' : 'var(--stone-800)' }}
        >
          {language === 'es' && '\u00bfEmpezamos?'}
          {language === 'en' && 'Shall we begin?'}
          {language === 'cat' && 'Comencem?'}
          {language === 'fr' && 'On commence\u00a0?'}
        </h1>
        {/* Gold decorative divider */}
        <div className="flex items-center justify-center gap-3 mt-4 animate-fade-up delay-100">
          <div style={{ width: '2rem', height: '2px', background: 'linear-gradient(to right, transparent, rgba(200,155,60,0.8))' }} />
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#D4AF37' }} />
          <div style={{ width: '2rem', height: '2px', background: 'linear-gradient(to left, transparent, rgba(200,155,60,0.8))' }} />
        </div>
      </div>

      {/* CAPA A: Modo Momento Banner — curated recommendation */}
      {!bannerDismissed && (
        <MomentoBanner 
          onViewExperience={(expProduct) => {
            if (expProduct) setBannerExperience(expProduct);
          }}
          onDismiss={() => setBannerDismissed(true)} 
        />
      )}

      {/* Experiences Banner */}
      <div className="px-4 mb-4 max-w-5xl mx-auto">
        <button
          onClick={() => setShowExperiencesShowcase(true)}
          className="w-full rounded-2xl overflow-hidden shadow-md active:scale-[0.98] transition-transform"
          style={{
            background: isDark
              ? 'linear-gradient(135deg, #2a2418 0%, #3d3020 100%)'
              : 'linear-gradient(135deg, #F5ECD7 0%, #EDE3C8 50%, #E8DAB4 100%)',
            border: isDark ? '1px solid rgba(183,140,60,0.2)' : '1px solid rgba(183,140,60,0.25)',
          }}
          data-testid="experiences-banner-btn"
        >
          <div className="flex items-center gap-4 p-4 sm:p-5">
            <img
              src="/pont10-logo-10.png"
              alt=""
              aria-hidden="true"
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex-shrink-0 object-contain"
              style={{ background: '#000' }}
            />
            <h3
              className="font-serif font-semibold flex-1 text-left text-2xl sm:text-3xl"
              style={{ color: isDark ? '#f0e6d0' : '#2C2C2A', letterSpacing: '-0.01em' }}
            >
              {language === 'es' ? 'Experiencias Pont10' :
               language === 'en' ? 'Pont10 Experiences' :
               language === 'cat' ? 'Experiències Pont10' :
               'Expériences Pont10'}
            </h3>
            <ChevronRight size={28} style={{ color: '#B78C3C' }} />
          </div>
        </button>
      </div>

      {/* Experience Cards — time-based order */}
      <div className="px-4 pb-8 max-w-5xl mx-auto w-full flex-shrink-0">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {experiences.map((exp, index) => {
            const Icon = exp.icon;
            return (
              <button
                key={exp.id}
                onClick={() => handleExperienceClick(exp.id)}
                className={`experience-card animate-fade-up delay-${(index + 2) * 100}`}
                data-testid={`experience-card-${exp.id}`}
              >
                <img 
                  src={exp.image} 
                  alt={exp.names[language]}
                  loading="lazy"
                  decoding="async"
                />
                <div className="experience-card-content">
                  <div className="experience-card-icon">
                    <Icon size={24} />
                  </div>
                  <h2 className="experience-card-title">
                    {exp.names[language]}
                  </h2>
                  <p className="experience-card-subtitle">
                    {exp.subtitles[language]}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      </div>

      {/* ExperienceDetailPopup for banner experience */}
      {bannerExperience && (
        <ExperienceDetailPopup
          experience={bannerExperience}
          onClose={() => setBannerExperience(null)}
        />
      )}
    </div>
  );
};

export default ExperienceSelector;
